import sportableLogo from '@frontend/assets/images/sportable-logo-blue.svg'
import { useOrganisations_getAllQuery } from '@frontend/core/lib/domains/organisations'
import {
  selectAllRegions,
  selectSelectedRegion
} from '@frontend/core/lib/domains/regions'
import type { regions as regionsData } from '@frontend/core/lib/domains/regions/data_types'
import {
  setUnitSystem,
  unitSystemOptions
} from '@frontend/core/lib/domains/units'
import {
  useLogoutMutation,
  useWhoAmIQuery
} from '@frontend/core/lib/modules/authentication'
import { IMAGE_PRESETS } from '@frontend/core/lib/modules/images'
import {
  StaticAssetImage,
  useAppDispatch,
  useAppSelector,
  useSelectedOrganisationId
} from '@frontend/react'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Input,
  themeAtom
} from '@frontend/ui'
import { useAtom } from 'jotai/react'
import { Check, ChevronsUpDown, Loader, LogOut, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  switchReportingOrganisation,
  switchReportingRegion
} from '../lib/reporting-actions'
import { resolveUnitSystemChoice } from './units-menu-helpers'

export function ReportingUserMenu() {
  const { data: user } = useWhoAmIQuery()
  const [logout, { isLoading: isLogoutLoading }] = useLogoutMutation()
  const dispatch = useAppDispatch()

  const regions = useAppSelector(selectAllRegions)
  const selectedRegion = useAppSelector(selectSelectedRegion)
  const selectedUnitSystem = useAppSelector((state) => state.units.selected)

  const orgsQuery = useOrganisations_getAllQuery(undefined, {})
  const organisations = useMemo(
    () => Object.values(orgsQuery.data?.entities ?? {}),
    [orgsQuery.data?.entities]
  )
  const selectedOrgId = useSelectedOrganisationId()
  const selectedOrg = selectedOrgId
    ? orgsQuery.data?.entities[selectedOrgId]
    : undefined

  const [theme, setTheme] = useAtom(themeAtom)

  const [orgSearch, setOrgSearch] = useState('')
  const filteredOrgs = useMemo(() => {
    const q = orgSearch.trim().toLowerCase()
    if (!q) return organisations
    return organisations.filter((o) => o.name.toLowerCase().includes(q))
  }, [organisations, orgSearch])

  // While user data is reloading (e.g. after a region switch invalidates the
  // auth cache) render a disabled placeholder trigger so the nav doesn't
  // collapse.
  if (!user) {
    return (
      <Button
        variant='outline'
        disabled
        className='w-auto justify-between gap-2 bg-transparent'
      >
        <Loader className='h-4 w-4 animate-spin opacity-50' />
        <span className='text-muted-foreground truncate text-sm'>Loading…</span>
      </Button>
    )
  }

  const singleRegion = regions.length <= 1
  const singleOrg = organisations.length <= 1

  // Items that select region/org use preventDefault in onSelect so the menu
  // stays open — users can switch scope without re-opening the dropdown.
  const keepOpen = (e: Event) => e.preventDefault()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          className='w-auto justify-between gap-2 bg-transparent'
        >
          <Avatar className='h-6 w-6 rounded-full'>
            <StaticAssetImage
              src={user.image}
              alt={user.firstName}
              preset={IMAGE_PRESETS.playerThumbnail}
              fallback={
                <AvatarFallback className='rounded-full text-xs'>
                  {user.firstName.charAt(0)}
                </AvatarFallback>
              }
            />
          </Avatar>
          <span className='truncate font-medium'>{user.firstName}</span>
          <ChevronsUpDown className='h-4 w-4 opacity-50' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-72 rounded-lg'>
        {/* User header */}
        <DropdownMenuLabel className='p-0 font-normal'>
          <div className='flex items-center gap-2 px-2 py-1.5 text-left text-sm'>
            <Avatar className='h-8 w-8 rounded-full'>
              <StaticAssetImage
                src={user.image}
                alt={user.firstName}
                preset={IMAGE_PRESETS.playerThumbnail}
                fallback={
                  <AvatarFallback className='rounded-full'>
                    {user.firstName.charAt(0)}
                  </AvatarFallback>
                }
              />
            </Avatar>
            <div className='grid flex-1 text-left text-sm leading-tight'>
              <span className='truncate font-medium'>
                {user.firstName} {user.lastName}
              </span>
              <span className='text-muted-foreground truncate text-xs'>
                {user.email}
              </span>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Region */}
        <DropdownMenuLabel className='text-muted-foreground text-xs'>
          Region
        </DropdownMenuLabel>
        {singleRegion ? (
          <DropdownMenuItem disabled className='gap-2 p-2'>
            <Avatar className='h-6 w-6 rounded-lg'>
              <AvatarFallback className='rounded-lg bg-transparent'>
                {selectedRegion?.flag ?? '—'}
              </AvatarFallback>
            </Avatar>
            <span className='truncate'>{selectedRegion?.name ?? '—'}</span>
          </DropdownMenuItem>
        ) : (
          regions.map((r) => {
            const isSelected = selectedRegion?.id === r.id
            return (
              <DropdownMenuItem
                key={r.id}
                className='gap-2 p-2'
                onSelect={(e) => {
                  keepOpen(e)
                  dispatch(
                    switchReportingRegion(r.id as keyof typeof regionsData)
                  )
                }}
              >
                <Avatar className='h-6 w-6 rounded-lg'>
                  <AvatarFallback className='rounded-lg bg-transparent'>
                    {r.flag}
                  </AvatarFallback>
                </Avatar>
                <span className='flex-1 truncate'>{r.name}</span>
                {isSelected && <Check className='h-4 w-4 opacity-70' />}
              </DropdownMenuItem>
            )
          })
        )}

        <DropdownMenuSeparator />

        {/* Organisation */}
        <DropdownMenuLabel className='text-muted-foreground text-xs'>
          Organisation
        </DropdownMenuLabel>
        {singleOrg ? (
          <DropdownMenuItem disabled className='gap-2 p-2'>
            <Avatar className='h-6 w-6 rounded-lg'>
              <AvatarImage
                src={selectedOrg ? sportableLogo : ''}
                alt={selectedOrg?.name ?? 'Organisation'}
              />
              <AvatarFallback className='rounded-lg'>
                {selectedOrg?.name?.charAt(0) ?? '?'}
              </AvatarFallback>
            </Avatar>
            <span className='truncate'>{selectedOrg?.name ?? '—'}</span>
          </DropdownMenuItem>
        ) : (
          <>
            <div
              className='relative px-1 py-1'
              // Radix DropdownMenu uses typeahead + arrow keys; without this
              // the menu intercepts letters you type in the search input.
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Search className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2' />
              <Input
                name='orgSearch'
                value={orgSearch}
                onChange={setOrgSearch}
                placeholder='Search organisations…'
                className='h-8 pl-8 text-sm'
              />
            </div>
            <div className='max-h-56 overflow-y-auto'>
              {filteredOrgs.length === 0 ? (
                <div className='text-muted-foreground px-2 py-1.5 text-xs'>
                  No matches
                </div>
              ) : (
                filteredOrgs.map((o) => {
                  const isSelected = selectedOrgId === o.id
                  return (
                    <DropdownMenuItem
                      key={o.id}
                      className='gap-2 p-2'
                      onSelect={(e) => {
                        keepOpen(e)
                        dispatch(switchReportingOrganisation(o.id))
                      }}
                    >
                      <Avatar className='h-6 w-6 rounded-lg'>
                        <AvatarImage src={sportableLogo} alt={o.name} />
                        <AvatarFallback className='rounded-lg'>
                          {o.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className='flex-1 truncate'>{o.name}</span>
                      {isSelected && <Check className='h-4 w-4 opacity-70' />}
                    </DropdownMenuItem>
                  )
                })
              )}
            </div>
          </>
        )}

        <DropdownMenuSeparator />

        {/* Appearance */}
        <DropdownMenuLabel className='text-muted-foreground text-xs'>
          Theme
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            if (value === 'light' || value === 'dark') setTheme(value)
          }}
        >
          <DropdownMenuRadioItem value='light'>Light</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value='dark'>Dark</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        {/* Units — collapsed into a submenu (rather than an always-expanded
            radio group) so 6 more options don't inflate the top-level menu.
            Grid layout puts "Units" as a header row (spanning both columns,
            matching the Region/Organisation/Theme DropdownMenuLabel style)
            above the current value, which shares its row with the trigger's
            built-in chevron via grid auto-placement. */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className='grid grid-cols-[1fr_auto] items-center gap-x-2 gap-y-1 py-2'>
            <span className='text-muted-foreground col-span-2 text-xs font-medium'>
              Units
            </span>
            <span>
              {unitSystemOptions.find((o) => o.value === selectedUnitSystem)
                ?.name ?? selectedUnitSystem}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={selectedUnitSystem}
              onValueChange={(value) => {
                const choice = resolveUnitSystemChoice(value)
                if (choice) dispatch(setUnitSystem(choice))
              }}
            >
              {unitSystemOptions.map((unitSystem) => (
                <DropdownMenuRadioItem
                  key={unitSystem.value}
                  value={unitSystem.value}
                >
                  {unitSystem.name}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => {
            if (isLogoutLoading) return
            logout()
          }}
        >
          {isLogoutLoading ? (
            <>
              <Loader className='h-4 w-4 animate-spin' />
              Logging out…
            </>
          ) : (
            <>
              <LogOut className='h-4 w-4' />
              Log out
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
